import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import { ApiError, type CompanyGroupDetailResponse, type CompanyGroupMember } from '../../api';
import {
  AsyncSection,
  DataTable,
  EmptyState,
  ErrorState,
  Form,
  FormError,
  PageHeader,
  Panel,
  SubmitButton,
  Tabs,
  TextField,
  fieldErrors,
  useNotifications,
  type Column,
  type Tab,
} from '../../components';
import { formatCnpj, formatDate } from '../../format';
import { PATHS, staffCompanyPath } from '../../routes/paths';
import { useGroup, useRenameGroup, useSetCompanyGroup } from '../queries';
import { AddCompanyDialog } from './AddCompanyDialog';
import { GroupFileNameLabelPanel } from './FileNameLabelPanel';
import { GroupBaselineImport } from './GroupBaselineImport';
import { GroupSeguradosPanel } from './GroupSeguradosPanel';
import { UsersPanel } from './UsersPanel';
import { GROUP_TABS, GROUP_TABS_LABEL, type GroupTab } from './groupTabs';
import { GROUPS_COPY } from './groups';

/**
 * One economic group, at an address of its own: its name, its companies, and
 * the things done to it -- rename it, put a company in, take one out, and set
 * the name its members' insurer files carry when they have none of their own.
 *
 * Membership is set on the company (`PUT /companies/{id}/group`), so adding and
 * removing here are the same write the company's own page makes. The member
 * list is whatever `GET /groups/{id}` last said, never patched by hand: the
 * write invalidates the group, the groups list and the companies, and what
 * appears is what the server now reports.
 *
 * Below the header the page is four tabs, the section in `?aba=` and read and
 * written by [`Tabs`](../../components/tabs/Tabs.tsx), with the same slugs the
 * company page uses for the tabs the two share. A bare address opens Geral, and
 * so does a value that names no tab. Only the open tab is mounted, so its
 * panels fire their queries when it is opened; the group itself, the header and
 * the rename draft are held above the strip and survive every change of tab.
 *
 * **Geral** is the group's name and its companies: the rename form, with the
 * slug shown read-only, then the members. A company comes in through the
 * "Adicionar empresa" button on the members panel, which opens a search over
 * the registry, paged by the server, newest first and by default only
 * companies in no group (#428) -- see [`AddCompanyDialog`](./AddCompanyDialog.tsx).
 * A tick widens it to companies in another group, and choosing one of those
 * moves it: its row and the sentence in the dialog say so before the click.
 *
 * **Validações e regras** is the name the members' insurer files carry when they
 * have none of their own, read from the group already held here -- see
 * [`FileNameLabelPanel`](./FileNameLabelPanel.tsx).
 *
 * **Segurados** is who the group insures today -- see
 * [`GroupSeguradosPanel`](./GroupSeguradosPanel.tsx) -- and under it the group's
 * initial-population import: the customer's one sheet, loaded into the
 * contracts of every company here -- see
 * [`GroupBaselineImport`](./GroupBaselineImport.tsx).
 *
 * **Usuários** is the people registered at the group itself (#391), who act for
 * any company currently in it -- the company page's users panel, read with
 * `?group_id=` and created with `group_id` in the body; see
 * [`UsersPanel`](./UsersPanel.tsx).
 *
 * There is no delete. The API has none, and so neither does the screen.
 */
export function GroupDetailPage() {
  const { grupoId = '' } = useParams();
  const group = useGroup(grupoId);

  // A stale or mistyped address is a `404`, and says so with the way back
  // rather than as a failure with a retry button that can only fail again.
  if (group.error instanceof ApiError && group.error.isNotFound) {
    return (
      <div className="page">
        <PageHeader title={GROUPS_COPY.title} description={GROUPS_COPY.description} />
        <EmptyState
          title={GROUPS_COPY.notFound}
          description={GROUPS_COPY.notFoundDescription}
          action={
            <Link className="button button--secondary" to={PATHS.staffGroups}>
              {GROUPS_COPY.back}
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="page">
      <AsyncSection
        state={group}
        loadingLabel={GROUPS_COPY.groupLoading}
        errorTitle={GROUPS_COPY.groupError}
      >
        {/* Keyed on the group so that going from one to another starts the
            rename form over rather than carrying a half-typed name across. */}
        {(data) => <GroupDetail key={data.id} group={data} />}
      </AsyncSection>
    </div>
  );
}

function GroupDetail({ group }: { group: CompanyGroupDetailResponse }) {
  const rename = useRenameGroup();
  const remove = useSetCompanyGroup();
  const { notify } = useNotifications();

  const [nameDraft, setNameDraft] = useState(group.name);
  const [nothingChanged, setNothingChanged] = useState(false);
  const [adding, setAdding] = useState(false);

  const renameErrors = fieldErrors(rename.error);

  const submitRename = () => {
    const name = nameDraft.trim();
    if (name === group.name) {
      // Restating the name answers 200 and records nothing, which reads to a
      // person as "saved" -- so the screen says what actually happened.
      setNothingChanged(true);
      return;
    }
    setNothingChanged(false);
    rename.mutate(
      { groupId: group.id, name },
      { onSuccess: () => notify('success', GROUPS_COPY.renamed) },
    );
  };

  const removeMember = (member: CompanyGroupMember) => {
    remove.mutate(
      { companyId: member.id, groupId: null },
      { onSuccess: () => notify('success', `${member.name} saiu do grupo.`) },
    );
  };

  const columns: readonly Column<CompanyGroupMember>[] = [
    {
      key: 'name',
      header: GROUPS_COPY.companyNameLabel,
      render: (row) => <Link to={staffCompanyPath(row.id)}>{row.name}</Link>,
    },
    {
      key: 'slug',
      header: GROUPS_COPY.companySlugLabel,
      render: (row) => <code>{row.slug}</code>,
    },
    {
      key: 'cnpj',
      header: GROUPS_COPY.companyCnpjLabel,
      render: (row) => <code>{formatCnpj(row.cnpj)}</code>,
    },
    {
      key: 'actions',
      header: GROUPS_COPY.actionsLabel,
      align: 'end',
      render: (row) => (
        <button
          type="button"
          className="button button--secondary"
          disabled={remove.isPending}
          onClick={() => removeMember(row)}
        >
          {GROUPS_COPY.remove}
        </button>
      ),
    },
  ];

  const content: Record<GroupTab, () => ReactNode> = {
    geral: () => (
      <>
        <Panel title={GROUPS_COPY.renameTitle} description={GROUPS_COPY.renameDescription}>
          <Form onSubmit={submitRename} aria-label={GROUPS_COPY.renameTitle}>
            <FormError error={rename.error} />
            {nothingChanged && (
              <p className="field__error" role="alert">
                {GROUPS_COPY.nothingChanged}
              </p>
            )}
            <TextField
              label={GROUPS_COPY.nameLabel}
              value={nameDraft}
              onChange={setNameDraft}
              hint={GROUPS_COPY.nameHint}
              required
              {...(renameErrors['name'] !== undefined && { error: renameErrors['name'] })}
            />
            {/* Shown, never editable: the API has no field for it, and a slug
                that could change would break every place it was written down. */}
            <p className="field__hint">
              {GROUPS_COPY.slugLabel}: <code>{group.slug}</code>. {GROUPS_COPY.slugReadOnly}
            </p>
            <div className="form__actions">
              <SubmitButton pending={rename.isPending} pendingLabel={GROUPS_COPY.savingName}>
                {GROUPS_COPY.saveName}
              </SubmitButton>
            </div>
          </Form>
        </Panel>

        <Panel
          title={GROUPS_COPY.membersTitle}
          description={GROUPS_COPY.membersDescription}
          actions={
            <button
              type="button"
              className="button button--primary"
              onClick={() => setAdding(true)}
            >
              {GROUPS_COPY.addTitle}
            </button>
          }
        >
          {group.members.length === 0 ? (
            <EmptyState
              title={GROUPS_COPY.noMembers}
              description={GROUPS_COPY.noMembersDescription}
            />
          ) : (
            <DataTable
              id="group-members"
              caption={GROUPS_COPY.membersCaption}
              columns={columns}
              rows={group.members}
              rowKey={(row) => row.id}
            />
          )}

          {/* A refused removal has nowhere to land inside the table, and a toast
              would take the reference code with it. */}
          {remove.isError && <ErrorState error={remove.error} title={GROUPS_COPY.removeRefused} />}
        </Panel>
      </>
    ),
    regras: () => <GroupFileNameLabelPanel group={group} />,
    segurados: () => (
      <>
        {/* Everyone the group's companies insure today, one row per person. */}
        <GroupSeguradosPanel group={group} />

        {/* The group's initial population, from the one sheet covering every
            company in it. Its panels are the company import's own. */}
        <GroupBaselineImport group={group} />
      </>
    ),
    // The people registered at the group rather than at one of its companies.
    usuarios: () => <UsersPanel scope={{ kind: 'group', groupId: group.id }} />,
  };

  const tabs: readonly Tab[] = (Object.keys(GROUP_TABS) as GroupTab[]).map((slug) => ({
    slug,
    label: GROUP_TABS[slug],
    content: content[slug],
  }));

  return (
    <>
      <PageHeader
        title={group.name}
        description={`${GROUPS_COPY.description} Cadastrado em ${formatDate(group.created_at)}.`}
        actions={
          <Link className="button button--ghost" to={PATHS.staffGroups}>
            {GROUPS_COPY.back}
          </Link>
        }
      />

      <Tabs tabs={tabs} label={GROUP_TABS_LABEL} />

      <AddCompanyDialog group={group} open={adding} onClose={() => setAdding(false)} />
    </>
  );
}
